import { useEffect, useState } from "react"
import Modal from "Components/molecule/modal"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import SelectInput from "Components/molecule/inputs/select-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import DateInput from "Components/molecule/inputs/date-input"
import {
    BillingFrequency, ExpenseBudget, ExpenseBudgetCurrency, ExpenseBudgetLine, ExpenseBudgetLinePayload, ExpenseBudgetVendor,
} from "models/expense-budget"
import {
    billingPeriodFromRenewal, billsInYear, fromPercent, isRenewalBased, rateToCad, toPercent,
} from "utils/expense-budget"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import { formatCurrency } from "utils/tools"

interface ProductFormModalProps {
    isOpen: boolean
    budget: ExpenseBudget
    line: ExpenseBudgetLine | null
    // Vendor chosen when the modal is opened from a vendor row.
    defaultVendorId: string | null
    isSaving: boolean
    onClose: () => void
    onSave: (payload: ExpenseBudgetLinePayload) => Promise<void>
    onCreateVendor: (name: string) => Promise<ExpenseBudgetVendor | null>
}

type ProductFormValues = Record<
    "vendorId" | "productName" | "quantity" | "currency" | "unitPrice" | "taxPercent" |
    "billingFrequency" | "firstBillingPeriod" | "renewalDate", string
>

const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

const FREQUENCY_LABELS: Record<BillingFrequency, string> = {
    [BillingFrequency.Monthly]: "Monthly",
    [BillingFrequency.Quarterly]: "Quarterly",
    [BillingFrequency.Annually]: "Annually",
    [BillingFrequency.OneTime]: "One time",
}

const toFormValues = (budget: ExpenseBudget, line: ExpenseBudgetLine | null, defaultVendorId: string | null): ProductFormValues => ({
    vendorId: line?.expenseBudgetVendorId ?? defaultVendorId ?? "",
    productName: line?.productName ?? "",
    quantity: line?.quantity ? String(Number(line.quantity)) : "1",
    currency: line?.currency ?? ExpenseBudgetCurrency.CAD,
    unitPrice: line?.unitPrice ? String(Number(line.unitPrice)) : "",
    taxPercent: String(toPercent(line?.taxRate ?? budget.defaultTaxRate)),
    billingFrequency: line?.billingFrequency ?? BillingFrequency.Monthly,
    firstBillingPeriod: String(line?.firstBillingPeriod ?? budget.fiscalYearId * 100 + 1),
    renewalDate: line?.renewalDate?.slice(0, 10) ?? "",
})

// Monthly bills run the whole fiscal year; annual and quarterly land in the renewal month;
// one-time bills in the month chosen.
const billingPeriodOf = (budget: ExpenseBudget, values: ProductFormValues): number | null => {
    const frequency = values.billingFrequency as BillingFrequency
    if (frequency === BillingFrequency.Monthly) return budget.fiscalYearId * 100 + 1
    if (frequency === BillingFrequency.OneTime) return Number(values.firstBillingPeriod)
    return values.renewalDate ? billingPeriodFromRenewal(values.renewalDate, budget.fiscalYearId) : null
}

const ProductFormModal = ({
    isOpen, budget, line, defaultVendorId, isSaving, onClose, onSave, onCreateVendor,
}: ProductFormModalProps) => {
    const [values, setValues] = useState<ProductFormValues>(toFormValues(budget, line, defaultVendorId))
    const [newVendorName, setNewVendorName] = useState<string | null>(null)

    // Reset only when the modal opens: the budget reloads after "+ New vendor" and must not wipe the form.
    useEffect(() => {
        if (!isOpen) return
        setValues(toFormValues(budget, line, defaultVendorId))
        setNewVendorName(null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, line, defaultVendorId])

    const handleChange = (key: keyof ProductFormValues, value: string) => setValues(prev => ({ ...prev, [key]: value }))

    const handleCreateVendor = async () => {
        if (!newVendorName?.trim()) return
        const vendor = await onCreateVendor(newVendorName.trim())
        if (!vendor) return
        handleChange("vendorId", vendor.id)
        setNewVendorName(null)
    }

    const frequency = values.billingFrequency as BillingFrequency
    const billingPeriod = billingPeriodOf(budget, values)
    const rate = rateToCad(budget, values.currency as ExpenseBudgetCurrency)
    const charge = Number(values.quantity) * Number(values.unitPrice) * rate * (1 + fromPercent(Number(values.taxPercent)))
    const annualCad = billingPeriod ? charge * billsInYear(frequency, billingPeriod % 100) : 0

    const vendorOptions = budget.vendors.map(v => ({ value: v.id, label: v.name }))
    const currencyOptions = Object.values(ExpenseBudgetCurrency).map(c => ({ value: c, label: c }))
    const frequencyOptions = Object.values(BillingFrequency).map(f => ({ value: f, label: FREQUENCY_LABELS[f] }))
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: String(budget.fiscalYearId * 100 + i + 1),
        label: calendar.fiscalToLabel(budget.fiscalYearId, i + 1),
    }))
    const canSave = values.vendorId && values.productName.trim() && values.unitPrice !== "" && billingPeriod !== null && rate > 0

    const handleSave = () => onSave({
        expenseBudgetVendorId: values.vendorId,
        productName: values.productName.trim(),
        quantity: Number(values.quantity),
        currency: values.currency as ExpenseBudgetCurrency,
        unitPrice: Number(values.unitPrice),
        taxRate: fromPercent(Number(values.taxPercent)),
        billingFrequency: frequency,
        firstBillingPeriod: billingPeriod!,
        renewalDate: values.renewalDate || null,
    })

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={line ? "Edit Product" : "New Product"}>
            <div className="d-flex flex-column gap-2">
                {newVendorName === null ? (
                    <div className="d-flex gap-2 align-items-end">
                        <SearchSelect label="Vendor" placeholder="Type to search" options={vendorOptions} containerStyle={{ flex: 1 }}
                            value={values.vendorId} onChange={option => handleChange("vendorId", option?.value ?? "")} />
                        <ActionButton onClick={() => setNewVendorName("")}>+ New vendor</ActionButton>
                    </div>
                ) : (
                    <div className="d-flex gap-2 align-items-end">
                        <TextInput label="New vendor name" value={newVendorName} autoFocus onChange={e => setNewVendorName(e.target.value)}
                            onKeyDown={e => { if (e.key === "Enter") handleCreateVendor() }} />
                        <ActionButton disabled={!newVendorName.trim()} onClick={handleCreateVendor}>Add</ActionButton>
                        <ActionButton onClick={() => setNewVendorName(null)}>Cancel</ActionButton>
                    </div>
                )}
                <TextInput label="Product / License" value={values.productName} onChange={e => handleChange("productName", e.target.value)} />
                <div className="d-flex gap-2">
                    <TextInput label="Quantity" type="number" min={0} value={values.quantity} onChange={e => handleChange("quantity", e.target.value)} />
                    <SelectInput label="Currency" options={currencyOptions} value={values.currency} onChange={e => handleChange("currency", e.target.value)} />
                    <TextInput label="Unit price" type="number" min={0} value={values.unitPrice} onChange={e => handleChange("unitPrice", e.target.value)} />
                    <TextInput label="Tax %" type="number" min={0} value={values.taxPercent} onChange={e => handleChange("taxPercent", e.target.value)} />
                </div>
                <div className="d-flex gap-2">
                    <SelectInput label="Billing" options={frequencyOptions} value={values.billingFrequency}
                        onChange={e => handleChange("billingFrequency", e.target.value)} />
                    {frequency === BillingFrequency.OneTime ? (
                        <SelectInput label="Bill month" options={monthOptions}
                            value={values.firstBillingPeriod} onChange={e => handleChange("firstBillingPeriod", e.target.value)} />
                    ) : (
                        <DateInput label={isRenewalBased(frequency) ? "Renewal date" : "Renewal date (optional)"}
                            value={values.renewalDate} onChange={e => handleChange("renewalDate", e.target.value)} />
                    )}
                </div>
                {rate === 0 && <span className="text-danger">Set the {values.currency} exchange rate in the Settings tab first.</span>}
                <div className="d-flex flex-column">
                    <strong>Annual: {formatCurrency(annualCad)} CAD (tax incl.)</strong>
                    {billingPeriod && isRenewalBased(frequency) &&
                        <span className="text-muted">
                            Billed in {calendar.periodToMonth(billingPeriod % 100, "en-CA")}
                            {frequency === BillingFrequency.Quarterly ? " and every 3 months" : ""}. Amortized ÷ 12 in the Accounting view.
                        </span>}
                    {isRenewalBased(frequency) && !values.renewalDate && <span className="text-muted">Enter the renewal date to set the bill month.</span>}
                </div>
                <div className="d-flex justify-content-end gap-2">
                    <ActionButton onClick={onClose}>Cancel</ActionButton>
                    <ActionButton disabled={!canSave || isSaving} onClick={handleSave}>{isSaving ? "Saving..." : "Save"}</ActionButton>
                </div>
            </div>
        </Modal>
    )
}

export default ProductFormModal
